import styled from '@emotion/styled'
import { DragDropContext, Droppable } from '@hello-pangea/dnd'
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Restore,
  Save as SaveIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Fab,
  Menu,
  MenuItem,
  Radio,
  Typography,
} from '@mui/material'
import { pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import { snakeCase } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import CenteredBox from '@/components/CenteredBox'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'

import { useEditPlanogramLogic } from '../hooks/use-edit-planogram.logic'
import AddProductControls from './AddProductControls'
import PlanogramHistory from './PlanogramHistory'
import PlanogramPDF from './PlanogramPDF'
import RenderedPlanogram from './RenderedPlanogram'
import SelectedItems from './SelectedItems'
import SelectedShelves from './SelectedShelves'
import Shelf from './Shelf'
import SnapshotList from './SnapshotList'

const HISTORY_PREVIEW_HEIGHT = 320

const PlanogramContainer = styled.div`
  flex: 2;
  padding: 16px;
  gap: 8px;
  position: relative;
  /* flexbox */
  display: flex;
  flex-direction: column;
`

const RightPanel = styled.div`
  display: flex;
  flex: 1;
  min-width: 350;
  flex-direction: column;
  padding: 16px;
  border-left: 1px solid #aaa;
  gap: 16px;
`

const EditPlanogram: React.FC = () => {
  const { displayId } = useParams()
  const { t } = useT()
  const { user } = useLoggedInUser()

  const {
    displayName,
    storeName,
    editing,
    setEditing,
    savingChanges,
    state,
    setState,
    snapshot,
    setSnapshot,
    originalState,
    loading,
    onDragEnd,
    onDragStart,
    selectedShelves,
    selectedItems,
    clearSelections,
    toggleSelectShelf,
    onFacingClick,
    handleDeleteShelves,
    handleDeleteProducts,
    handleCopyProducts,
    handleCopyShelves,
    handlePasteShelf,
    clipboardAvailable,
    clipboardSource,
    handleSave,
    selectedProducts,
    matchingPlanogramFacingCount,
    productToAdd,
    setProductToAdd,
    originalStateIsEqual,
    modal,
    setModal,
  } = useEditPlanogramLogic(displayId)

  const productInfoByUpc = useMemo(
    () =>
      Object.fromEntries(
        originalState.flat().map(({ product }) => [product.upcA, product]),
      ),
    [originalState],
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [loadingPDF, setLoadingPDF] = useState(false)
  const planogramViewportRef = useRef<HTMLDivElement>(null)
  const [planogramMaxHeight, setPlanogramMaxHeight] = useState<number>()
  const hasOriginalState = !!originalState.length

  useEffect(() => {
    if (!planogramViewportRef.current) return
    const el = planogramViewportRef.current
    const updateHeight = () => {
      const { top } = el.getBoundingClientRect()
      setPlanogramMaxHeight(
        Math.max(window.innerHeight - top - HISTORY_PREVIEW_HEIGHT, 200),
      )
    }
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(el)
    window.addEventListener('resize', updateHeight)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateHeight)
    }
  }, [hasOriginalState, editing])

  const handleGeneratePDF = async (reversed = false) => {
    setLoadingPDF(true)
    try {
      const blob = await pdf(
        <PlanogramPDF
          t={t}
          displays={[
            {
              name: displayName,
              storeName,
              reversed,
              products: state.map(shelf => shelf.map(facing => facing.product)),
            },
          ]}
        />,
      ).toBlob()

      const filePrefix = `${snakeCase(displayName) || 'display'}`
      saveAs(
        blob,
        reversed
          ? `${filePrefix}_planogram_back.pdf`
          : `${filePrefix}_planogram_front.pdf`,
      )
    } finally {
      setLoadingPDF(false)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {(savingChanges || loading) && !editing ? (
        <CenteredBox>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
            {savingChanges && (
              <Typography variant="h6">
                {t('editPlanogram.savingChanges')}
              </Typography>
            )}
          </Box>
        </CenteredBox>
      ) : editing ? (
        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
          <Divider />
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              height: '100%',
              width: '100%',
            }}
          >
            <PlanogramContainer>
              {savingChanges || loading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 4,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress />
                  {savingChanges && (
                    <Typography variant="h6">
                      {t('editPlanogram.savingChanges')}
                    </Typography>
                  )}
                </Box>
              ) : (
                <>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: '8px',
                    }}
                  >
                    {state.map((shelf, shelfIndex) => (
                      <div
                        key={shelfIndex}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Radio
                          checked={selectedShelves.includes(shelfIndex)}
                          onClick={() => toggleSelectShelf(shelfIndex)}
                        />
                        <Shelf
                          selectedIndices={selectedItems
                            .filter(item => item.rowIndex === shelfIndex)
                            .map(item => item.index)}
                          onFacingClick={onFacingClick}
                          onFacingDoubleClick={(shelfIndex, columnIndex) => {
                            if (user?.isSuperuser) {
                              const product =
                                state[shelfIndex][columnIndex].product
                              window.open(
                                `https://admin.deliciousai.com/Products?upc=${product.upcA}&searchTerm=${product.upcA}`,
                                '_blank',
                              )
                            }
                          }}
                          facings={shelf}
                          listId={shelfIndex.toString()}
                        />
                      </div>
                    ))}
                    {!state.length && (
                      <Alert severity="info">{t('editPlanogram.prompt')}</Alert>
                    )}
                    <Droppable droppableId="new-shelf" type="CARD">
                      {provided => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            backgroundColor: '#ffa8',
                            minHeight: '50px',
                            border: '1px dashed #ccc',
                            margin: '8px 0',
                            borderRadius: '4px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Typography color="textPrimary">
                            {t('editPlanogram.newShelfLabel')}
                          </Typography>
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      position: 'sticky',
                      bottom: '16px',
                      right: '16px',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    <Fab
                      variant={'extended'}
                      onClick={() => {
                        setState(originalState)
                        setEditing(false)
                        setSnapshot(null)
                        setModal(null)
                      }}
                    >
                      <CancelIcon sx={{ mr: 1 }} />
                      {t('editPlanogram.button.cancel')}
                    </Fab>
                    <Fab
                      variant={'extended'}
                      onClick={() => {
                        setState(originalState)
                      }}
                      disabled={originalStateIsEqual}
                    >
                      <Restore sx={{ mr: 1 }} />
                      {t('editPlanogram.button.reset')}
                    </Fab>
                    <Fab
                      variant={'extended'}
                      onClick={handleSave}
                      disabled={originalStateIsEqual}
                    >
                      <SaveIcon sx={{ mr: 1 }} />
                      {t('editPlanogram.button.saveChanges')}
                    </Fab>
                    {modal !== 'snapshot' && (
                      <Fab
                        variant={'extended'}
                        onClick={() => {
                          clearSelections()
                          setModal('snapshot')
                        }}
                      >
                        <ImageIcon sx={{ mr: 1 }} />
                        {t('editPlanogram.button.snapshots')}
                      </Fab>
                    )}
                    {modal !== 'add-product' && (
                      <Fab
                        color="primary"
                        aria-label="add"
                        onClick={() => {
                          clearSelections()
                          setModal('add-product')
                        }}
                      >
                        <AddIcon />
                      </Fab>
                    )}
                  </Box>
                </>
              )}
            </PlanogramContainer>

            {selectedProducts.length > 0 ? (
              <RightPanel>
                <SelectedItems
                  selectedProducts={selectedProducts.map(item => item.product)}
                  matchingPlanogramFacingCount={matchingPlanogramFacingCount}
                  clearSelections={clearSelections}
                  handleDeleteProducts={handleDeleteProducts}
                  handleCopyProducts={handleCopyProducts}
                />
              </RightPanel>
            ) : selectedShelves.length > 0 ? (
              <RightPanel>
                <SelectedShelves
                  selectedShelves={selectedShelves}
                  clearSelections={clearSelections}
                  handleDeleteShelves={handleDeleteShelves}
                  handleCopyShelves={handleCopyShelves}
                  handlePasteShelf={() => {
                    void handlePasteShelf()
                  }}
                  clipboardAvailable={clipboardAvailable}
                  clipboardSource={clipboardSource}
                />
              </RightPanel>
            ) : modal === 'add-product' ? (
              <RightPanel>
                <AddProductControls
                  onClose={() => setModal(null)}
                  setProductToAdd={setProductToAdd}
                  productToAdd={productToAdd}
                />
              </RightPanel>
            ) : (
              modal === 'snapshot' && (
                <RightPanel>
                  <SnapshotList
                    displayId={displayId}
                    snapshot={snapshot}
                    onClose={() => setModal(null)}
                    setSnapshot={setSnapshot}
                  />
                </RightPanel>
              )
            )}
          </Box>
        </DragDropContext>
      ) : (
        <Box
          ref={planogramViewportRef}
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            overflowY: 'auto',
            py: 1,
          }}
        >
          <Box sx={{ width: 'min(88vw, 920px)' }}>
            {originalState.length ? (
              <RenderedPlanogram
                products={originalState.map(shelf =>
                  shelf.map(item => item.product),
                )}
                shelfHeight={108}
                maxHeight={
                  planogramMaxHeight ? planogramMaxHeight - 16 : undefined
                }
                productInfoByUpc={productInfoByUpc}
              />
            ) : (
              <Alert severity="info">{t('editPlanogram.noPlanogram')}</Alert>
            )}
          </Box>
          {displayId && <PlanogramHistory displayId={displayId} />}
        </Box>
      )}
      {!editing && (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            position: 'sticky',
            bottom: '16px',
            right: '16px',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <>
            <Fab
              variant={'extended'}
              aria-label="print"
              onClick={handleClick}
              sx={{ cursor: 'pointer', gap: 1 }}
              disabled={loadingPDF || loading}
            >
              {loadingPDF && <CircularProgress size={24} />}
              <DownloadIcon />
              {t('editPlanogram.button.print')}
            </Fab>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  handleClose()
                  handleGeneratePDF()
                }}
              >
                {t('editPlanogram.button.printRegular')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose()
                  handleGeneratePDF(true)
                }}
              >
                {t('editPlanogram.button.printReversed')}
              </MenuItem>
            </Menu>
          </>

          <Fab
            variant={'extended'}
            color="primary"
            aria-label="edit"
            onClick={() => {
              clearSelections()
              setEditing(true)
            }}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
          >
            <EditIcon sx={{ mr: 1 }} />
            {t('editPlanogram.button.edit')}
          </Fab>
        </Box>
      )}
    </div>
  )
}

export default EditPlanogram
